import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, type ReactNode } from 'react'

import { api } from '@/lib/api-client'

export interface AuthUser {
  id: string
  email: string
  name: string
  emailVerified: boolean
  role?: string | null
  permissions?: readonly string[]
  [extra: string]: unknown
}

interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function fetchSession(): Promise<AuthUser | null> {
  try {
    const { data } = await api.get<{
      user?: AuthUser
      permissions?: readonly string[]
    } | null>('/api/auth/get-session')
    if (!data?.user) return null
    return { ...data.user, permissions: data.permissions ?? [] }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: fetchSession,
    staleTime: 60_000,
    retry: false,
  })

  const value: AuthContextValue = {
    user: data ?? null,
    isLoading,
    isAuthenticated: !!data,
    refresh: async () => {
      await refetch()
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider />')
  return ctx
}
